import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

const STEPS: {
  title: string;
  body: string;
  entity?: string;
  entityLabel?: string;
  /** The click path to the surface this step describes, when there is one. */
  where?: [string, ...string[]];
  whereNote?: string;
  /** A diagram shown right after the body. */
  figure?: ReactNode;
}[] = [
  {
    title: 'Name the change first',
    body: "Three different operations look similar from the customer's side. A migration moves an existing pin to a newer published version of the same module/bundle/component. A plan change swaps the composition itself — a different bundle, a different module set or tier. An expansion records an addition on top of the current plan — a raised commitment, an added module. Picking the right one keeps the version history and the MRR movement classification honest.",
    entity: 'expansion',
    entityLabel: 'Expansion',
    figure: (
      <Figure
        title="Which operation fits the change"
        caption="Pick by what changes: the version, the composition, or something added on top."
      >
        <Flow
          steps={[
            { title: 'Customer changes', text: 'Something about the current plan is different.' },
            {
              fork: [
                {
                  title: 'Migration',
                  text: 'Moves an existing pin to a newer published version.',
                },
                {
                  title: 'Plan change',
                  text: 'Swaps the composition: another bundle, module set or tier.',
                },
                {
                  title: 'Expansion',
                  text: 'Adds on top of the plan, such as a raised commitment or module.',
                  tone: 'accent',
                },
              ],
            },
          ]}
        />
      </Figure>
    ),
  },
  {
    title: 'Preview the money',
    body: 'calculatePriceChange previews the pricing impact of a proposed change; calculateProratedAmount previews just the prorated charge or credit for the remainder of the cycle. Both are read-only. Every dashboard flow shows this preview before the confirm button for a reason.',
  },
  {
    title: 'Choose the timing',
    body: 'Migrations and plan changes take an explicit timing: MID_CYCLE_PRORATED applies now and issues an adjustment invoice for the remainder of the period; NEXT_RENEWAL queues a pendingMigration that the renewal boundary applies cleanly, with no mid-cycle invoice. Queued changes can be cleared with cancelPendingMigration any time before they apply.',
    entity: 'subscription',
    entityLabel: 'Subscription',
    figure: (
      <Figure
        title="Timing of a migration or plan change"
        caption="Apply now with an adjustment invoice, or queue it for the renewal boundary."
      >
        <Flow
          steps={[
            { title: 'Migration or plan change', text: 'Takes an explicit timing.' },
            {
              fork: [
                {
                  title: 'MID_CYCLE_PRORATED',
                  text: 'Applies now. Adjustment invoice for the rest of the period.',
                  tone: 'accent',
                },
                {
                  title: 'NEXT_RENEWAL',
                  text: 'Queued as pendingMigration. Applied at renewal, no mid-cycle invoice.',
                },
              ],
            },
          ]}
        />
      </Figure>
    ),
    where: ['Subscriptions', 'open a subscription', 'the actions menu', 'Change plan or version'],
    whereNote:
      'The same modal previews the money and takes the timing, so the figure and the decision are on one screen.',
  },
  {
    title: 'How proration is calculated',
    body: "The proration model comes from the Variable Component (or the subscription's base terms): FULL_MONTH charges the month of the change in full plus every month left in the period (a seat added in month 3 of a yearly term bills 10 of 12 months), DAILY charges by days remaining, NONE skips proration entirely. A mid-cycle upgrade from $49 to $79 with 14 of 30 days left under DAILY prorates to (79 − 49) × 14⁄30 ≈ $14.",
  },
  {
    title: 'A change inside a prepaid span asks about the cash',
    body: 'If the customer has already paid for periods ahead, proration alone would charge the price difference for the current period only — every remaining prepaid period bills nothing, so the rest of the span would silently keep the old price. Revvly refuses to guess and asks: "Charge the difference now" raises one catch-up invoice covering the rest of the current period plus every whole period the prepayment still covers, and "Do not charge until the span runs out" applies the change and bills nothing more. Downward, "charge now" is a credit note, symmetrically — the money for those periods is already in. The plan changes immediately either way and MRR moves with it; this decides cash only. The catch-up is a one-off proration line, never a rate, so nothing restates MRR at a figure no renewal will charge.',
    entity: 'invoice',
    entityLabel: 'Invoice',
    where: [
      'Subscriptions',
      'open a subscription',
      'Change plan or version',
      'Inside a prepaid span',
    ],
    whereNote:
      'The choice appears only when the subscription is inside a live prepaid span, and the modal will not submit without it.',
  },
  {
    title: 'Expansions land in the record',
    body: 'createSubscriptionExpansion writes the expansion with its prorated amount and effective date, and emits an EXPANDED (or CONTRACTED) lifecycle event with the signed MRR delta. That delta is what Expansion MRR and NRR are built from — expansions are not just invoices, they are analytics inputs.',
    entity: 'lifecycleEvent',
    entityLabel: 'Lifecycle Event',
  },
  {
    title: 'Everything is in the version history',
    body: 'Every pin move, plan change, component attach/detach, and grandfathering decision appends to the subscription\'s versionHistory — queryable via subscriptionVersionHistory, oldest first. When someone asks "what changed on this account and when," that log is the answer.',
    entity: 'subscription',
    entityLabel: 'Subscription',
  },
];

export function MidCycleChangesGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Handle a mid-cycle change — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Mid-cycle changes</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Handle a <em>mid-cycle</em> change.
        </h1>
        <p className="dp-oneliner">
          Migration, plan change, or expansion — how to tell them apart, preview the proration, and
          pick between applying now and waiting for the renewal boundary.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.figure}
          {step.where && <UiPath steps={step.where} note={step.whereNote} />}
          {step.entity && (
            <ul className="dp-connects" style={{ marginTop: 14 }}>
              <li>
                <Link
                  to={`/docs/concepts/${step.entity}`}
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  {step.entityLabel} concept ›
                </Link>
              </li>
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
